import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { LogOut, RefreshCw, Check, Inbox, Download } from "lucide-react";
import { api } from "../lib/api";
import { useTitle } from "../lib/useTitle";

const TABS = [
  { key: "referral", label: "Referrals" },
  { key: "application", label: "Applications" },
  { key: "volunteer", label: "Volunteers" },
  { key: "booking", label: "Event Reservations" },
  { key: "contact", label: "Contact Messages" },
  { key: "newsletter", label: "Community Signups" },
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const csvCell = (v) => {
  if (v == null) return "";
  if (Array.isArray(v)) v = v.join("; ");
  if (typeof v === "boolean") v = v ? "Yes" : "No";
  return `"${String(v).replace(/"/g, '""')}"`;
};

const exportCsv = (tabKey, items) => {
  const flat = items.map((item) => {
    const row = {
      date: fmtDate(item.created_at),
      name: item.name || "",
      email: item.email || "",
      phone: item.phone || "",
      subject: item.subject || "",
      message: item.message || "",
      followed_up: item.followed_up || false,
    };
    if (item.details) {
      Object.entries(item.details).forEach(([k, v]) => {
        row[k.replace(/_/g, " ")] = v;
      });
    }
    return row;
  });
  const headers = [...new Set(flat.flatMap((r) => Object.keys(r)))];
  const csv = [headers.map(csvCell).join(","), ...flat.map((r) => headers.map((h) => csvCell(r[h])).join(","))].join("\r\n");
  const blob = new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `bee-courageous-${tabKey}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.3h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.3-2.1 3.7-5.2 3.7-8.8z" />
    <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.1-6.8-5.1L1.3 17.2C3.3 21.3 7.3 24 12 24z" />
    <path fill="#FBBC05" d="M5.2 14.3c-.2-.7-.4-1.5-.4-2.3s.2-1.6.4-2.3L1.3 6.8C.5 8.4 0 10.1 0 12s.5 3.6 1.3 5.2l3.9-2.9z" />
    <path fill="#EA4335" d="M12 4.6c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1 15.2 0 12 0 7.3 0 3.3 2.7 1.3 6.8l3.9 2.9c.9-3 3.6-5.1 6.8-5.1z" />
  </svg>
);

export default function Admin() {
  useTitle("Team Sign-In | Bee Courageous @ Garden Grub");
  const location = useLocation();
  const [token, setToken] = useState(() => localStorage.getItem("bc_admin_token") || "");
  const [googleUser, setGoogleUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!localStorage.getItem("bc_admin_token") && !location.state?.user);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(location.state?.authError || "");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("referral");

  const authed = Boolean(token || googleUser);
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    if (token || googleUser) return;
    let cancelled = false;
    api.get("/auth/me")
      .then((res) => { if (!cancelled) setGoogleUser(res.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    try {
      const res = await api.get("/admin/overview", { headers: authHeaders });
      setData(res.data);
    } catch {
      setToken("");
      setGoogleUser(null);
      localStorage.removeItem("bc_admin_token");
    }
  };

  useEffect(() => {
    if (authed) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await api.post("/auth/login", { email, password });
      localStorage.setItem("bc_admin_token", res.data.token);
      setToken(res.data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Sign-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const onFlag = async (collection, id, current) => {
    await api.patch("/admin/flag", { collection, id, followed_up: !current }, { headers: authHeaders });
    load();
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* cookie cleanup best-effort */ }
    localStorage.removeItem("bc_admin_token");
    setToken("");
    setGoogleUser(null);
    setData(null);
  };

  if (!authed) {
    return (
      <section className="grain honeycomb-bg flex min-h-screen items-center justify-center px-4 pt-20" data-testid="admin-login">
        <div className="card-soft w-full max-w-md !border-wing/20">
          <h1 className="font-serif text-3xl font-semibold text-charcoal">Team Sign-In</h1>
          <p className="mt-2 text-sm text-charcoal/60">Private area for Bee Courageous @ Garden Grub leadership.</p>

          <button
            type="button"
            onClick={googleSignIn}
            className="mt-6 flex w-full items-center justify-center gap-3 rounded-full border-2 border-charcoal/15 bg-white px-6 py-3.5 text-sm font-semibold text-charcoal transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:border-wing/40 hover:shadow-lg"
            data-testid="admin-google-btn"
          >
            <GoogleIcon /> Continue with Google
          </button>
          <p className="mt-2 text-center text-xs text-charcoal/50">For Sandra and Meg — use your Bee Courageous Gmail</p>

          <div className="my-5 flex items-center gap-3 text-xs font-bold uppercase tracking-widest text-charcoal/40">
            <span className="h-px flex-1 bg-charcoal/15" /> or with password <span className="h-px flex-1 bg-charcoal/15" />
          </div>

          <form onSubmit={onLogin}>
            <div className="space-y-4">
              <div>
                <label htmlFor="admin-email" className="label-field">Email</label>
                <input id="admin-email" type="email" required className="input-field" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" />
              </div>
              <div>
                <label htmlFor="admin-password" className="label-field">Password</label>
                <input id="admin-password" type="password" required className="input-field" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" />
              </div>
              {error && <p className="rounded-xl bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-700" data-testid="admin-login-error">{error}</p>}
              {checking && <p className="text-center text-xs text-charcoal/50">Checking your sign-in…</p>}
              <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60" data-testid="admin-login-submit">
                {loading ? "Signing in…" : "Sign In"}
              </button>
            </div>
          </form>
        </div>
      </section>
    );
  }

  const itemsFor = (key) => {
    if (!data) return [];
    if (key === "contact") return data.contacts.map((c) => ({ ...c, _collection: "contact_messages" }));
    if (key === "newsletter") return data.subscribers;
    return data.submissions.filter((s) => s.type === key).map((s) => ({ ...s, _collection: "submissions" }));
  };

  const counts = Object.fromEntries(TABS.map((t) => [t.key, itemsFor(t.key).length]));
  const items = itemsFor(tab);

  return (
    <section className="min-h-screen bg-cream px-4 pb-20 pt-28 sm:px-6 lg:px-12" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <span className="eyebrow">Team Dashboard</span>
            <h1 className="font-serif text-3xl font-semibold text-charcoal sm:text-4xl">Submissions & messages</h1>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => exportCsv(tab, items)}
              disabled={!items.length}
              className="btn-outline !px-5 !py-2.5 disabled:opacity-50"
              data-testid="admin-export-btn"
            >
              <Download className="h-4 w-4" aria-hidden="true" /> Download CSV
            </button>
            <button onClick={() => load()} className="btn-outline !px-5 !py-2.5" data-testid="admin-refresh-btn">
              <RefreshCw className="h-4 w-4" aria-hidden="true" /> Refresh
            </button>
            <button onClick={logout} className="btn-outline !px-5 !py-2.5" data-testid="admin-logout-btn">
              <LogOut className="h-4 w-4" aria-hidden="true" /> Sign Out
            </button>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Submission types">
          {TABS.map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`rounded-full border px-4 py-2 text-xs font-bold transition-colors duration-200 ${
                tab === t.key ? "border-wing bg-wing text-white" : "border-wing/25 bg-white text-charcoal/70 hover:border-wing"
              }`}
            >
              {t.label} ({counts[t.key] ?? 0})
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-4">
          {!data && <p className="text-sm text-charcoal/60">Loading…</p>}
          {data && items.length === 0 && (
            <div className="card-soft flex items-center gap-4 text-charcoal/60" data-testid="admin-empty">
              <Inbox className="h-6 w-6" aria-hidden="true" /> Nothing here yet — new submissions will appear automatically.
            </div>
          )}
          {items.map((item) => (
            <article
              key={item.id}
              className={`card-soft !p-5 sm:!p-6 ${item.followed_up ? "opacity-60" : ""}`}
              data-testid={`admin-item-${item.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-serif text-lg font-semibold text-charcoal">{item.name || item.email}</h3>
                  <p className="text-xs font-bold uppercase tracking-widest text-charcoal/50">{fmtDate(item.created_at)}</p>
                </div>
                {item._collection && (
                  <button
                    onClick={() => onFlag(item._collection, item.id, item.followed_up)}
                    data-testid={`admin-flag-${item.id}`}
                    className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${
                      item.followed_up ? "bg-garden text-white" : "bg-pollen-soft text-pollen-dark hover:bg-pollen hover:text-white"
                    }`}
                  >
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                    {item.followed_up ? "Followed up" : "Mark followed up"}
                  </button>
                )}
              </div>
              <dl className="mt-3 grid gap-x-8 gap-y-1.5 text-sm sm:grid-cols-2">
                {item.email && <div className="flex gap-2"><dt className="font-bold text-garden">Email:</dt><dd>{item.email}</dd></div>}
                {item.phone && <div className="flex gap-2"><dt className="font-bold text-garden">Phone:</dt><dd>{item.phone}</dd></div>}
                {item.subject && <div className="flex gap-2"><dt className="font-bold text-garden">Subject:</dt><dd>{item.subject}</dd></div>}
                {item.message && <div className="sm:col-span-2 flex gap-2"><dt className="font-bold text-garden">Message:</dt><dd>{item.message}</dd></div>}
                {item.details && Object.entries(item.details).map(([k, v]) => (
                  <div key={k} className="flex gap-2 sm:col-span-2">
                    <dt className="shrink-0 font-bold capitalize text-garden">{k.replace(/_/g, " ")}:</dt>
                    <dd>{Array.isArray(v) ? v.join(", ") : typeof v === "boolean" ? (v ? "Yes" : "No") : String(v)}</dd>
                  </div>
                ))}
              </dl>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
